import { CardContent, CardActions, FormControl, FormLabel, Box } from "@mui/joy";
import { DeleteIcon, AddCircleIcon } from "../../../../styles/StyledMUI";
import type { QuestionTemplateForm } from "../../../../api/theoryTests/TheoryTest.typed";
import DarkTypography from "../../../common/DarkTypography";
import DarkInput from "../../../common/DarkInput";
import DarkCard from "../../../common/DarkCard";
import DarkCheckbox from "../../../common/DarkCheckbox";
import IconButtonCTA from "../../../common/IconButtonCTA";
import ActionTooltip from "../../../common/ActionTooltip";

interface QuestionFormCardProps {
  question: QuestionTemplateForm;
  onChange: (updatedQuestion: QuestionTemplateForm) => void;
  onDelete: () => void;
}

const QuestionFormCard = ({ question, onChange, onDelete }: QuestionFormCardProps) => {
  const handleCorrectAnswerChange = (answer: string) => {
    const newCorrectAnswers = question.CorrectAnswers.includes(answer)
      ? question.CorrectAnswers.filter((a: string) => a !== answer)
      : [...question.CorrectAnswers, answer];

    onChange({
      ...question,
      CorrectAnswers: newCorrectAnswers,
    });
  };

  const handleAnswerChange = (index: number, value: string) => {
    const newPossibleAnswers = [...question.PossibleAnswers];
    newPossibleAnswers[index] = value;
    onChange({ ...question, PossibleAnswers: newPossibleAnswers });
  };

  const handleAddAnswer = () => {
    onChange({
      ...question,
      PossibleAnswers: [...question.PossibleAnswers, ""],
    });
  };

  const handleRemoveAnswer = (index: number) => {
    const newPossibleAnswers = question.PossibleAnswers.filter(
      (_: string, i: number) => i !== index,
    );
    const newCorrectAnswers = question.CorrectAnswers.filter((answer: string) =>
      newPossibleAnswers.includes(answer),
    );
    onChange({
      ...question,
      PossibleAnswers: newPossibleAnswers,
      CorrectAnswers: newCorrectAnswers,
    });
  };

  return (
    <DarkCard>
      <CardContent>
        <FormControl sx={{ width: "100%", marginBottom: 2 }}>
          <FormLabel>
            <DarkTypography fontWeight="lg" sx={{ marginBottom: 1 }}>
              Question
            </DarkTypography>
          </FormLabel>
          <DarkInput
            placeholder="Saisissez votre question"
            value={question.QuestionText}
            onChange={(e) => onChange({ ...question, QuestionText: e.target.value })}
            fullWidth
          />
        </FormControl>

        <DarkCard dense sx={{ marginBottom: 2 }}>
          <DarkTypography fontWeight="lg" sx={{ marginBottom: 1 }}>
            Réponses
          </DarkTypography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {question.PossibleAnswers.map((answer: string, index: number) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <DarkCheckbox
                  checked={question.CorrectAnswers.includes(answer)}
                  onChange={() => handleCorrectAnswerChange(answer as string)}
                />
                <FormControl size="sm" sx={{ flexGrow: 1 }}>
                  <DarkInput
                    placeholder={`Réponse ${index + 1}`}
                    size="sm"
                    value={answer}
                    onChange={(e) => handleAnswerChange(index, e.target.value)}
                  />
                </FormControl>
                <ActionTooltip title="Supprimer réponse">
                  <IconButtonCTA
                    ctaVariant="danger"
                    aria-label="Supprimer réponse"
                    size="sm"
                    onClick={() => handleRemoveAnswer(index)}
                  >
                    <DeleteIcon />
                  </IconButtonCTA>
                </ActionTooltip>
              </Box>
            ))}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <ActionTooltip title="Ajouter réponse">
                <IconButtonCTA
                  ctaVariant="accent"
                  aria-label="Ajouter réponse"
                  size="sm"
                  onClick={handleAddAnswer}
                >
                  <AddCircleIcon />
                </IconButtonCTA>
              </ActionTooltip>
            </Box>
          </Box>
        </DarkCard>
      </CardContent>
      <CardActions sx={{ justifyContent: "flex-end" }}>
        <ActionTooltip title="Supprimer la question">
          <IconButtonCTA ctaVariant="danger" aria-label="Supprimer la question" onClick={onDelete}>
            <DeleteIcon />
          </IconButtonCTA>
        </ActionTooltip>
      </CardActions>
    </DarkCard>
  );
};

export default QuestionFormCard;
